// src/contexts/CommandRegistry.jsx
//
// Page-scoped command registry for the global Cmd/Ctrl+K palette.
//
// Any component mounted inside <CommandRegistryProvider> can register
// context-specific actions (e.g. "Acknowledge this alarm",
// "Assign person to location"). Commands are keyed by a stable id and
// removed automatically when the component unmounts.
//
// Usage in a page:
//   useCommand({
//     id: 'alarm.ack',
//     label: 'Acknowledge this alarm',
//     group: 'This page',
//     icon: <CheckCircleIcon />,
//     onRun: () => handleAcknowledge(),
//   }, [handleAcknowledge]);
//
// The palette picks up `useCommands()` in the layout and merges the
// registry with the static nav commands.

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

const CommandRegistryContext = createContext(null);

class Store {
  constructor() {
    this.map = new Map();
    this.listeners = new Set();
  }

  subscribe = (fn) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = () => this.map;

  register = (cmd) => {
    if (!cmd?.id) return () => {};
    this.map = new Map(this.map);
    this.map.set(cmd.id, cmd);
    this.listeners.forEach((fn) => fn());
    return () => {
      this.map = new Map(this.map);
      this.map.delete(cmd.id);
      this.listeners.forEach((fn) => fn());
    };
  };
}

export const CommandRegistryProvider = ({ children }) => {
  const storeRef = useRef(null);
  if (storeRef.current === null) storeRef.current = new Store();

  const api = useMemo(
    () => ({
      register: storeRef.current.register,
      subscribe: storeRef.current.subscribe,
      getSnapshot: storeRef.current.getSnapshot,
    }),
    [],
  );

  return (
    <CommandRegistryContext.Provider value={api}>
      {children}
    </CommandRegistryContext.Provider>
  );
};

/**
 * Register a single command while the calling component is mounted.
 * `deps` controls when the command is re-registered — follow the same
 * rules as useEffect deps.
 */
export const useCommand = (command, deps = []) => {
  const ctx = useContext(CommandRegistryContext);
  const register = ctx?.register;
  const stableRegister = useCallback(
    (cmd) => (register ? register(cmd) : () => {}),
    [register],
  );
  React.useEffect(() => {
    if (!command || !command.id) return undefined;
    return stableRegister(command);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableRegister, ...deps]);
};

/**
 * Read the current flat list of registered commands. Subscribes to
 * updates so the caller re-renders when pages register/unregister.
 */
export const useCommands = () => {
  const ctx = useContext(CommandRegistryContext);
  const subscribe = ctx?.subscribe || ((fn) => () => fn);
  const getSnapshot = ctx?.getSnapshot || (() => EMPTY);
  const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(() => Array.from(map.values()), [map]);
};

const EMPTY = new Map();

export default CommandRegistryContext;
